import React, { useState } from "react";
import { Button, Money } from "../../components/ui";
import { getFileUrl } from "../../core/config/api.js";
import { timeLeft } from "./timeLeft.js";
import "./stock-actions-panel.scss";

// The "what needs doing" section at the top of Core List, Ad List and Sales
// Pipeline (Stock Alert, 2026-11-02). One row per open action from
// /api/core-list/actions for THIS tab's list; the red pill on the tab and the
// sidebar badge count the same rows, so clearing one here drops both.
//
// "Mark as done" is for the whole store team, not just you: the backend
// stores it per store. It comes back by itself when the situation changes (a
// new lot, a listing that sells or goes off again), so the only undo offered
// is for the last one marked — a slip, not a history.

/** What is wrong, in a sentence. Sentence case throughout (FE #1137). */
const describe = (action) => {
  if (action.kind === "live_auction") {
    const source = action.lot?.source_label || "auction";
    return action.lot?.at_target
      ? `In a live auction on ${source}, currently under your buy target`
      : `In a live auction on ${source}, needs review`;
  }
  if (action.reason === "sold") return "Listing not created: the last one sold";
  if (action.reason === "off") return "Listing not created: the listing is switched off";
  return "Listing not created";
};

/**
 * The link that fixes it. Never-listed and sold go to the create-listing flow
 * pre-filled with the product (the card's Create; the sold-listing duplicate
 * stays on the card, where its side effects are visible). A switched-off
 * listing opens that listing, where it can be switched back on. A lot opens on
 * the auction site.
 */
const linkFor = (action) => {
  if (action.kind === "live_auction") {
    return action.lot?.url
      ? { href: action.lot.url, label: `Open on ${action.lot.source_label || "auction site"}` }
      : null;
  }
  if (action.reason === "off" && action.listing_id) {
    return { href: `/product/${action.listing_id}/edit/product`, label: "Open listing" };
  }
  return action.product_id
    ? { href: `/product/create/${action.product_id}`, label: "Create listing" }
    : null;
};

const ActionRow = ({ action, onMarkDone, busy }) => {
  const link = linkFor(action);
  const lot = action.lot;
  const imageUrl = action.image_file_id ? getFileUrl(action.image_file_id) : lot?.image_url || null;
  const left = lot ? timeLeft(lot.ends_at) : null;

  return (
    <li className={`stock-actions__row stock-actions__row--${action.kind}`}>
      <span className="stock-actions__thumb" aria-hidden="true">
        {imageUrl ? <img src={imageUrl} alt="" loading="lazy" /> : null}
      </span>

      <div className="stock-actions__body">
        <div className="stock-actions__ref">
          <strong>{action.reference || "Unknown reference"}</strong>
          <span className="stock-actions__name">
            {[action.brand, action.model].filter(Boolean).join(" · ")}
          </span>
        </div>
        <div className="stock-actions__what">{describe(action)}</div>
        {lot ? (
          <div className="stock-actions__lot">
            {lot.title ? <span className="stock-actions__lot-title">{lot.title}</span> : null}
            <span>
              {lot.current_bid_gbp != null ? (
                <>
                  Current bid <Money value={lot.current_bid_gbp} currency="GBP" decimals={0} />
                  {lot.bids === 0 ? " (no bids yet)" : null}
                </>
              ) : (
                "No bid yet"
              )}
            </span>
            {left ? <span>{left}</span> : null}
          </div>
        ) : null}
      </div>

      <div className="stock-actions__controls">
        {link ? (
          <Button
            size="sm"
            variant="secondary"
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.label}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          loading={busy}
          onClick={() => onMarkDone(action)}
          aria-label={`Mark as done: ${describe(action)} for ${action.reference || "this reference"}`}
        >
          Mark as done
        </Button>
      </div>
    </li>
  );
};

/**
 * @param {object[]} actions        this tab's open actions
 * @param {(a) => Promise} onMarkDone
 * @param {(a) => Promise} onUndo
 * @param {boolean} [isLoading]
 */
const StockActionsPanel = ({ actions = [], onMarkDone, onUndo, isLoading = false }) => {
  const [busyKey, setBusyKey] = useState(null);
  // The last one marked done, so a slip can be taken back. One at a time:
  // this is an undo, not a history.
  const [lastDone, setLastDone] = useState(null);
  const [error, setError] = useState(null);

  const markDone = async (action) => {
    setBusyKey(action.key);
    setError(null);
    try {
      await onMarkDone(action);
      setLastDone(action);
    } catch (e) {
      console.error("Failed to mark the action as done", e);
      setError("Couldn't mark that as done. Please try again.");
    } finally {
      setBusyKey(null);
    }
  };

  const undo = async () => {
    if (!lastDone) return;
    const action = lastDone;
    setLastDone(null);
    setError(null);
    try {
      await onUndo(action);
    } catch (e) {
      console.error("Failed to undo", e);
      setError("Couldn't undo that. Please try again.");
    }
  };

  if (isLoading && actions.length === 0) return null;

  return (
    <section className="stock-actions" aria-label="Pending actions">
      <header className="stock-actions__header">
        <h2 className="stock-actions__title">Actions</h2>
        {actions.length > 0 ? (
          <span className="stock-actions__count" aria-label={`${actions.length} pending`}>
            {actions.length}
          </span>
        ) : null}
      </header>

      {actions.length === 0 ? (
        <p className="stock-actions__empty">No pending actions. You're all caught up.</p>
      ) : (
        <ul className="stock-actions__list">
          {actions.map((action) => (
            <ActionRow
              key={action.key}
              action={action}
              busy={busyKey === action.key}
              onMarkDone={markDone}
            />
          ))}
        </ul>
      )}

      {lastDone ? (
        <p className="stock-actions__undo" role="status">
          Marked as done for your team.{" "}
          <button type="button" className="stock-actions__undo-btn" onClick={undo}>
            Undo
          </button>
        </p>
      ) : null}
      {error ? (
        <p className="stock-actions__error" role="alert">
          {error}
        </p>
      ) : null}
    </section>
  );
};

export default StockActionsPanel;
